/**
 * Derives Sentry's tunnel URL from a DSN: `https://<key>@<host>/<projectId>`
 * -> `https://<host>/api/<projectId>/envelope/`. Passed as `tunnel` to
 * Sentry.init so envelopes POST to a plain URL with no `?sentry_key=` /
 * `?sentry_version=` in the query string -- the pattern uBlock Origin and
 * EasyPrivacy block on. The DSN itself travels inside the envelope header
 * instead; confirmed against our Sentry that a request built this way gets
 * a 200 and one with no DSN anywhere gets a 401.
 *
 * Deliberately NOT `/api/sentry/tunnel` -- that exact path is on
 * EasyPrivacy's own blocklist.
 *
 * Returns undefined for an unset or malformed DSN, matching Sentry.init's
 * own dsn: undefined no-op behavior.
 */
export function sentryTunnelUrl(dsn: string | undefined): string | undefined {
    if (!dsn) {
        return undefined;
    }

    let url: URL;
    try {
        url = new URL(dsn);
    } catch {
        return undefined;
    }

    // The project ID is the DSN path's last segment -- usually the whole
    // path (`/6`), but a self-hosted Sentry behind a URL prefix can put it
    // deeper (`/prefix/6`).
    const segments = url.pathname.split('/').filter(Boolean);
    const projectId = segments[segments.length - 1];
    if (!projectId) {
        return undefined;
    }

    return `${url.protocol}//${url.host}/api/${projectId}/envelope/`;
}
